import type { UserSummary } from '@ssn/shared';
import { APP_ROLE_META } from '@ssn/shared';
import { initialsOf } from '@/lib/format';
import { cn } from './cn';

const SIZES = {
  xs: 'size-5 text-[9px]',
  sm: 'size-6 text-[10px]',
  md: 'size-8 text-xs',
  lg: 'size-11 text-sm',
} as const;

export function Avatar({
  user,
  size = 'sm',
  className,
}: {
  user: Pick<UserSummary, 'displayName' | 'avatarUrl'>;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const shared = cn(
    'shrink-0 rounded-full border border-border object-cover',
    SIZES[size],
    className,
  );

  if (user.avatarUrl) {
    return <img src={user.avatarUrl} alt="" aria-hidden className={shared} loading="lazy" />;
  }

  return (
    <span
      aria-hidden
      className={cn(
        shared,
        'inline-flex items-center justify-center bg-accent-soft font-semibold text-accent-text',
      )}
    >
      {initialsOf(user.displayName)}
    </span>
  );
}

/** Avatar plus name, the standard way a person is shown throughout the app. */
export function UserChip({
  user,
  size = 'sm',
  showRole = false,
  className,
}: {
  user: UserSummary | null;
  size?: keyof typeof SIZES;
  showRole?: boolean;
  className?: string;
}) {
  if (!user) {
    return <span className={cn('text-sm text-text-subtle', className)}>Unassigned</span>;
  }

  return (
    <span className={cn('inline-flex min-w-0 items-center gap-2', className)}>
      <Avatar user={user} size={size} />
      <span className="min-w-0">
        <span className="block truncate text-sm text-text">{user.displayName}</span>
        {showRole && (
          <span className="block truncate text-[11px] text-text-subtle">
            {APP_ROLE_META[user.appRole].shortLabel}
          </span>
        )}
      </span>
    </span>
  );
}
